import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MdClose } from 'react-icons/md';
import { useAuth } from '../../Context/AuthContext';
import { getAllProviderSettings } from '../../Services/ProviderService';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import DocumentUpload from '../Common/DocumentUpload';
import AddressSelectorFields from '../Common/AddressSelectorFields';
import CustomDatePicker from '../Common/CustomDatePicker';
import { toast } from 'react-toastify';
import CustomPhoneInput from '../Common/CustomPhoneInput';

const validationSchema = Yup.object({
  prefix: Yup.mixed().nullable(),
  reference_name: Yup.string().trim().required('Reference name is required'),
  degree: Yup.mixed().nullable(),
  specialty: Yup.string().trim().nullable(),
  reference_title: Yup.string().trim().nullable(),
  relationship_type: Yup.string().trim().nullable(),
  length_time: Yup.string().trim().nullable(),
  reference_date: Yup.date().nullable().typeError('Invalid date'),
  reference_status: Yup.string().trim().nullable(),
  facility_name: Yup.string().trim().nullable(),
  email: Yup.string().trim().nullable().email('Invalid email'),
  phone: Yup.string().trim().nullable(),
  fax: Yup.string().trim().nullable(),
  address: Yup.object({
    street: Yup.string().trim().nullable(),
    street2: Yup.string().trim().nullable(),
    city: Yup.string().trim().nullable(),
    state: Yup.string().trim().nullable(),
    country: Yup.string().trim().nullable(),
    zipcode: Yup.string().trim().nullable(),
  }).nullable(),
});

const emptyForm = {
  prefix: null,
  reference_name: '',
  degree: null,
  specialty: '',
  reference_title: '',
  relationship_type: '',
  length_time: '',
  reference_date: null,
  reference_status: '',
  facility_name: '',
  email: '',
  phone: '',
  fax: '',
  address: { street: '', street2: '', city: '', state: '', country: '', zipcode: '' },
  document: null,
};

const SETTING_TYPE_IDS = {
  prefix: 18,
  degree: 22,
};

const mapProviderSettingItem = (item) => ({
  value: item.id ?? item.Id ?? item.value ?? item.value_id ?? null,
  label: item.name ?? item.Name ?? item.code ?? String(item.id ?? ''),
});

export default function PeerReferencesModal({ isOpen, onClose, onSave, initialData, isLoading }) {
  const { token } = useAuth();
  const [providerSettings, setProviderSettings] = useState({ prefix: [], degree: [] });
  const [settingsLoading, setSettingsLoading] = useState(false);

  const initialValues = useMemo(() => {
    if (!initialData) return emptyForm;

    return {
      ...emptyForm,
      ...initialData,
      address: {
        ...emptyForm.address,
        ...(initialData.address ?? {}),
      },
      document:
        initialData.document || initialData.file_name || initialData.fileName || null,
    };
  }, [initialData]);

  const loadProviderSettings = async () => {
    if (!token) return;

    setSettingsLoading(true);
    try {
      const entries = Object.entries(SETTING_TYPE_IDS);
      const results = await Promise.all(
        entries.map(async ([key, typeId]) => {
          try {
            const response = await getAllProviderSettings(token, typeId);
            if (response?.is_success) {
              return {
                key,
                options: Array.isArray(response.data)
                  ? response.data.map(mapProviderSettingItem)
                  : [],
              };
            }else if (response?.is_success === false && response?.status_code === 500) {
              toast.error('Internal server error. Please contact support team.');
            }else {
              toast.error(response?.message || `Failed to load settings`);
              console.error(`Failed to load ${key} settings:`, response?.message || 'Unknown error');
            }
          } catch (error) {
            console.error(`Failed to load provider settings for ${key}:`, error);
          }
          return { key, options: [] };
        })
      );

      setProviderSettings(results.reduce((acc, item) => ({ ...acc, [item.key]: item.options }), {}));
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    loadProviderSettings();
  }, [token]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        id: values.id || null,
        prefix: values.prefix !== '' && values.prefix !== null && values.prefix !== undefined ? Number(values.prefix) : null,
        degree: values.degree !== '' && values.degree !== null && values.degree !== undefined ? Number(values.degree) : null,
        reference_date: values.reference_date || null,
        type: values.id ? 'update' : 'create',
      };
      onSave(payload);
    },
  });

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content border-0 rounded-4">
          <div className="modal-header border-bottom-0 px-4 py-2 d-flex justify-content-between align-items-center">
            <h5 className="modal-title fw-bold hc-text-primary m-0">
              {initialData?.reference_name ? 'Edit Peer Reference' : 'Add Peer Reference'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={isLoading} />
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body px-4 py-2">
              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Peer Reference Details</h6>
                <div className="row g-3">
                  <div className="col-lg-4">
                    <SearchableDropdown
                      label="Prefix"
                      placeholder="Select Option"
                      options={providerSettings.prefix}
                      value={formik.values.prefix ?? ''}
                      onChange={(value) => {
                        formik.setFieldValue('prefix', value || null);
                        formik.setFieldTouched('prefix', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={formik.touched.prefix && formik.errors.prefix ? 'is-invalid' : ''}
                      emptyMessage="No prefixes found"
                    />
                    {formik.touched.prefix && formik.errors.prefix ? (
                      <div className="invalid-feedback d-block">{formik.errors.prefix}</div>
                    ) : null}
                  </div>

                  <div className="col-lg-8">
                    <label className="form-label fw-semibold">Reference Name <span className="text-danger">*</span></label>
                    <input
                      {...formik.getFieldProps('reference_name')}
                      className={`form-control rounded-3 shadow-sm ${formik.touched.reference_name && formik.errors.reference_name ? 'is-invalid' : ''}`}
                      placeholder="Enter reference name"
                    />
                    <div className="invalid-feedback">{formik.errors.reference_name}</div>
                  </div>

                  <div className="col-lg-6">
                    <SearchableDropdown
                      label="Degree"
                      placeholder="Select Option"
                      options={providerSettings.degree}
                      value={formik.values.degree ?? ''}
                      onChange={(value) => {
                        formik.setFieldValue('degree', value || null);
                        formik.setFieldTouched('degree', true);
                      }}
                      disabled={settingsLoading}
                      loading={settingsLoading}
                      className={formik.touched.degree && formik.errors.degree ? 'is-invalid' : ''}
                      emptyMessage="No degrees found"
                    />
                    {formik.touched.degree && formik.errors.degree ? (
                      <div className="invalid-feedback d-block">{formik.errors.degree}</div>
                    ) : null}
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Specialty</label>
                    <input
                      {...formik.getFieldProps('specialty')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter specialty"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Reference Title</label>
                    <input
                      {...formik.getFieldProps('reference_title')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter reference title"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Relationship Type</label>
                    <input
                      {...formik.getFieldProps('relationship_type')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter relationship type"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Reference Date</label>

                    <CustomDatePicker
                      {...formik.getFieldProps('reference_date')}
                      formik={formik}
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Reference Status</label>
                    <select
                      name="reference_status"
                      value={formik.values.reference_status ?? ''}
                      onChange={(event) => formik.setFieldValue('reference_status', event.target.value)}
                      className="form-select rounded-3 shadow-sm"
                    >
                      <option value="">Select status</option>
                      <option value="1">Active</option>
                      <option value="2">Inactive</option>
                    </select>
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Length of Time Known</label>
                    <input
                      {...formik.getFieldProps('length_time')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter length of relationship"
                    />
                  </div>

                  <div className="col-lg-6">
                    <label className="form-label fw-semibold">Facility Name</label>
                    <input
                      {...formik.getFieldProps('facility_name')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter facility name"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Contact Information</h6>
                <div className="row g-3">
                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Email</label>
                    <input
                      type="email"
                      {...formik.getFieldProps('email')}
                      className={`form-control rounded-3 shadow-sm ${formik.touched.email && formik.errors.email ? 'is-invalid' : ''}`}
                      placeholder="Enter email"
                    />
                    <div className="invalid-feedback">{formik.errors.email}</div>
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Phone</label>
                    {/* <CustomPhoneInput
                    name="phone"
                    {...formik.getFieldProps('phone')}
                      formik={formik}
                      defaultCountry="us"
                    /> */}
                    <input
                      {...formik.getFieldProps('phone')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter phone"
                    />
                  </div>

                  <div className="col-lg-4">
                    <label className="form-label fw-semibold">Fax</label>
                    <input
                      {...formik.getFieldProps('fax')}
                      className="form-control rounded-3 shadow-sm"
                      placeholder="Enter fax"
                    />
                  </div>
                </div>
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Document</h6>
                <DocumentUpload
                  id="peer-reference-document-input"
                  label="Upload Document"
                  value={formik.values.document}
                  onChange={(file) => formik.setFieldValue('document', file)}
                />
              </div>

              <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
                <h6 className="fw-bold hc-text-primary mb-3">Address</h6>
                <AddressSelectorFields
                  title="Address"
                  fieldPrefix="address"
                  values={formik.values.address}
                  errors={formik.errors.address || {}}
                  touched={formik.touched.address || {}}
                  onFieldChange={(fieldName, value) => formik.setFieldValue(fieldName, value)}
                  renderAsterisk={() => null}
                />
              </div>
            </div>

            <div className="modal-footer border-top-0 p-4 d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-light rounded-3 px-4" onClick={onClose} disabled={isLoading}>Cancel</button>
              <button type="submit" className="btn hc-bg-primary text-white rounded-3 px-4 d-flex align-items-center gap-2" disabled={isLoading}>
                {isLoading ? <span className="spinner-border spinner-border-sm text-white" role="status" aria-hidden="true" /> : null}
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
